import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import { DrawerBodyWrapper, DrawerFooterWrapper, SectionHeader, Textarea, TOAST_TYPE } from '@base'
import { Button, Pill } from '@baseV2'
import { strings } from '@constants'
import { editEnquiry, editOrder } from '@data'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer } from '@data/state/action/root'
import { Spacings, Text } from '@styles'

const AutoExtractedWrapper = styled.div`
	display: flex;
	justify-content: flex-end;
	margin-top: ${Spacings.SPACING_2B};
`

const TncWrapper = styled.div`
	margin-top: ${Spacings.SPACING_5B};
`

const TncValue = styled.div`
	margin-left: ${Spacings.SPACING_2B};
	margin-right: ${Spacings.SPACING_2B};
	font-size: ${Text.MEDIUM};
	line-height: ${Text.LINE_HEIGHT_MAX};
`

const TncList = styled.ul`
	margin-left: ${Spacings.SPACING_2B};
	margin-right: ${Spacings.SPACING_2B};
	padding-left: ${Spacings.SPACING_4B};
	list-style: disc;
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_1B};
	font-size: ${Text.MEDIUM};
	line-height: ${Text.LINE_HEIGHT_MAX};
`

const BuyerPaymentTerms = ({ enquiry, order, update }) => {
	const dispatch = useDispatch()
	const user = useSelector((state) => state.getIn(['user', 'user']))
	const originalPaymentTerms = enquiry?.payment_terms ?? order?.buyer_list?.[0]?.payment_terms ?? ''
	const [buyerPaymentTerms, setBuyerPaymentTerms] = useState(originalPaymentTerms)
	// nothing to save until the text actually differs — editing and undoing disables Save again
	const isUnchanged = buyerPaymentTerms === originalPaymentTerms

	const isAutoExtracted = enquiry?.payment_terms_auto_extracted ?? order?.buyer_list?.[0]?.payment_terms_auto_extracted ?? false

	const costMetrics = enquiry ? enquiry?.cost_metrics : order?.cost_metrics
	const tncAsPerBuyerPo = costMetrics?.tnc_as_per_buyer_po ?? []

	const closeDrawer = () => dispatch(toggleDrawer(false))

	// order flow: patch buyer_list[0] and PUT the whole order.
	// enquiry flow: PUT the enquiry (existing behavior, unchanged).
	const saveHandler = () => {
		dispatch(showLoader(true))
		const request = order
			? editOrder({
					...order,
					buyer_list: (order?.buyer_list ?? []).map((b, index) => (index === 0 ? { ...b, payment_terms: buyerPaymentTerms } : b)),
			  })
			: editEnquiry({ enquiry: { ...enquiry, payment_terms: buyerPaymentTerms }, user })

		request
			.then(() => {
				update?.()
				dispatch(showToast(true, strings('payment_terms_updated_success'), { type: TOAST_TYPE.SUCCESS }))
				closeDrawer()
			})
			.catch((error) => {
				const error_msg = error?.error_msg ?? strings('msg_payment_terms_update_failure')
				dispatch(showToast(true, error_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => dispatch(showLoader(false)))
	}

	return (
		<>
			<DrawerBodyWrapper>
				<Textarea
					id='buyer-payment-terms'
					label='Buyer Payment Terms'
					type='text'
					rows={10}
					placeholder='Enter payment terms as per Buyer PO'
					value={buyerPaymentTerms}
					onChange={(val) => setBuyerPaymentTerms(val)}
				/>
				{isAutoExtracted && (
					<AutoExtractedWrapper>
						<Pill color='error' size='small'>
							Generated by System
						</Pill>
					</AutoExtractedWrapper>
				)}
				{tncAsPerBuyerPo.length > 0 && (
					<TncWrapper>
						<SectionHeader>TNC As Per Buyer PO</SectionHeader>
						{tncAsPerBuyerPo.length === 1 ? (
							<TncValue>{tncAsPerBuyerPo[0]}</TncValue>
						) : (
							<TncList>
								{tncAsPerBuyerPo.map((tnc, index) => (
									<li key={`${index}-${tnc}`}>{tnc}</li>
								))}
							</TncList>
						)}
					</TncWrapper>
				)}
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<Button variant='solid' onClick={saveHandler} disabled={isUnchanged}>
					{strings('save')}
				</Button>
				<Button color='danger' variant='outline' style={{ marginRight: '0.75rem' }} onClick={closeDrawer}>
					{strings('cancel')}
				</Button>
			</DrawerFooterWrapper>
		</>
	)
}

export default BuyerPaymentTerms
